import React, { useCallback, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import size from 'lodash/size';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { useSelector } from 'react-redux';

import get from 'lodash/get';
import Button from '../Button';
import Popup from '../common/Popup';
import { enhance } from './enhance';
import Loader from '../common/Loader';
import ErrorBlock from '../common/ErrorBlock';
import SingleSelect from '../common/SingleSelect';
import { isDesktop } from '../../utils/mobileHelpers';
import ExpandableListItem from '../ExpandableListItem';
import { TASK_ATTACHMENT_LIMIT } from '../../constants/tasks';
import { timeToShow } from '../Healthboard/utils/handleTaskTime';
import { COMPLETED_STATUSES, TODO_STATUSES } from '../../constants';
import { selectActiveTodoItem } from '../../redux/healthboard/selectors';
import { TaskDocuments } from '../Healthboard/Components/TaskDetailsPopup/TaskDocuments';

import styles from './styles.styl';

const CarePlanDetailsPopup = ({
  id,
  error,
  clear,
  itemId,
  succeed,
  pending,
  onClose,
  complete,
  userId,
  intl: { formatMessage },
}) => {
  const [outcomeId, setOutcomeId] = useState(null);
  const [attachments, setAttachment] = useState([]);

  const activeItem = useSelector(selectActiveTodoItem);
  const {
    name,
    status,
    todoTask,
    description,
    expiredTime,
    chosenOutcome,
    completedTime,
    carePlanTodoType,
    taskOutcomes = [],
    outcomeAttachments,
    assigner: { displayableName },
  } = activeItem;

  const links = todoTask ? todoTask.links : [];

  const activeOutcome = useMemo(
    () => taskOutcomes.find(el => el.id === outcomeId),
    [outcomeId, taskOutcomes],
  );

  const completedOutcome = useMemo(
    () => taskOutcomes.find(el => el.id === chosenOutcome),
    [outcomeId, taskOutcomes],
  );

  const attachmentRequired = get(activeOutcome, 'attachmentEnabled');

  const disabled = useMemo(() => {
    const noRequiredAttachment = !size(attachments) && attachmentRequired;

    return (
      carePlanTodoType === 'base' &&
      (!outcomeId ||
        pending ||
        size(attachments) > TASK_ATTACHMENT_LIMIT ||
        noRequiredAttachment) &&
      taskOutcomes.length
    );
  }, [pending, outcomeId, attachments, attachmentRequired]);

  const completed = useMemo(() => COMPLETED_STATUSES.includes(status), [
    status,
  ]);

  const note = timeToShow(completedTime, expiredTime, null, formatMessage);

  const subtitle = formatMessage(
    { id: 'carePlan.assignedBy.provider' },
    { displayableName },
  );
  const titleId = useMemo(() => {
    switch (status) {
      case TODO_STATUSES.CANCELED:
        return 'task.canceled';
      case TODO_STATUSES.COMPLETED:
        return 'task.completed';
      default:
        return 'task.upcoming';
    }
  }, [status]);

  const outcomesList = useMemo(
    () =>
      taskOutcomes.map(item => {
        return {
          value: item.id,
          label: item.text,
        };
      }),
    [taskOutcomes],
  );

  const handleComplete = () => {
    const data = { id, userId, itemId, outcomeId, attachments };
    complete(data);
  };

  const handleOptionChange = useCallback(value => {
    setOutcomeId(value);
  }, []);

  const showDocuments = completed
    ? !isEmpty(outcomeAttachments)
    : attachmentRequired;

  const completedOutcomeText = get(completedOutcome, 'text');

  const errorText = useMemo(
    () =>
      error &&
      (get(error, 'description') || formatMessage({ id: 'errors.common' })),
    [error],
  );

  useEffect(() => {
    if (succeed) onClose();
  }, [succeed]);

  useEffect(() => () => clear(), []);

  return (
    <Popup
      strictClose
      onClose={onClose}
      opened={Boolean(itemId)}
      title={formatMessage({ id: titleId })}
      width={isDesktop() ? 'small' : 'medium'}
    >
      <ExpandableListItem
        locked
        key={itemId}
        data-id={itemId}
        header={{
          note,
          subtitle,
          title: name,
          icon: 'assigned-task',
        }}
      >
        <If condition={error}>
          <ErrorBlock className="mt2 mb2" errors={errorText} />
        </If>
        <If condition={pending}>
          <Loader className="absolute-center" />
        </If>
        <If condition={Boolean(description)}>
          <p className={styles.description}>{description}</p>
        </If>
        <If condition={!isEmpty(links)}>
          {links.map(link => (
            <a
              target="_blank"
              key={get(link, 'id')}
              className={styles.link}
              rel="noopener noreferrer"
              href={get(link, 'urlLink')}
            >
              {get(link, 'urlDescription')}
            </a>
          ))}
        </If>
        <If condition={Boolean(taskOutcomes.length && !completed)}>
          <div className={styles.section}>
            <h3 className={styles.title}>
              {formatMessage({ id: 'task.selectOutcome' })}
            </h3>
            <SingleSelect
              name="outcome"
              list={outcomesList}
              radioInputClass="mr2"
              selectClass={styles.list}
              onChange={handleOptionChange}
              selectedValue={outcomeId}
            />
          </div>
        </If>

        <If condition={Boolean(completedOutcomeText) && completed}>
          <div className={styles.section}>
            <h3 className={styles.title}>
              {formatMessage({ id: 'task.outcome' })}
            </h3>
            <p>{completedOutcomeText}</p>
          </div>
        </If>
        <If condition={showDocuments}>
          <div className={styles.section}>
            <h3 className={styles.title}>
              {formatMessage({ id: 'common.documents' })}
            </h3>
            <TaskDocuments
              completed={completed}
              formatMessage={formatMessage}
              addAttachment={setAttachment}
              disabled={size(attachments) >= TASK_ATTACHMENT_LIMIT}
              attachments={completed ? outcomeAttachments : attachments}
            />
          </div>
        </If>

        <If condition={!completed && !error}>
          <Button
            color="primary"
            disabled={disabled}
            onClick={handleComplete}
            className={cn(styles.completed, { mt0: Boolean(description) })}
          >
            {formatMessage({ id: 'common.complete' })}
          </Button>
        </If>
      </ExpandableListItem>
    </Popup>
  );
};

CarePlanDetailsPopup.propTypes = {
  id: PropTypes.number,
  clear: PropTypes.func,
  error: PropTypes.object,
  succeed: PropTypes.bool,
  pending: PropTypes.bool,
  itemId: PropTypes.number,
  onClose: PropTypes.func,
  complete: PropTypes.func,
  userId: PropTypes.number,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }),
};

export default enhance(CarePlanDetailsPopup);



// WEBPACK FOOTER //
// ./src/components/CarePlanDetailsPopup/index.jsx